/**
 * Bundle Cart Upsell — ajoute les items manquants au panier pour compléter le bundle.
 * - Recalcule la remise -15% en temps réel quand l'utilisateur change un variant (ex: cendre 50g → 200g)
 * - Discount Shopify Automatic appliqué au checkout pour la vraie remise serveur
 */
(function () {
  'use strict';

  const FORMATTER = new Intl.NumberFormat('fr-FR', {
    style: 'currency',
    currency: 'EUR',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

  class BundleCartUpsell extends HTMLElement {
    connectedCallback() {
      if (this._initialized) return;
      this._initialized = true;

      this.btn = this.querySelector('[data-bind="upsell-cta"]');
      this.savingsEl = this.querySelector('[data-bind="upsell-savings"]');
      this.errorEl = this.querySelector('[data-bind="upsell-error"]');
      this.bundleType = this.dataset.bundleType;
      this.cartAddUrl = this.dataset.routesCartAdd || '/cart/add.js';
      this.discountRate = parseFloat(this.dataset.discountRate) || 0.15;
      this.cartEligibleCents = parseInt(this.dataset.cartEligibleCents, 10) || 0;

      try {
        this.missingItems = JSON.parse(this.dataset.missingItems || '[]');
      } catch (e) {
        this.missingItems = [];
      }

      this.querySelectorAll('[data-bind="upsell-variant-picker"]').forEach((picker) => {
        picker.addEventListener('change', (e) => this._onPickerChange(e.target));
      });

      if (this.btn) {
        this.btn.addEventListener('click', () => this.activate());
      }

      this._recalc();

      this._track('bundle_cart_upsell_viewed', {
        bundle_type: this.bundleType,
        missing_count: this.missingItems.length,
      });
    }

    _onPickerChange(picker) {
      const card = picker.closest('.drawer__bundle-upsell-item');
      if (!card) return;
      const partnerProductId = parseInt(card.dataset.partnerProductId, 10);
      const opt = picker.options[picker.selectedIndex];
      const newVariantId = parseInt(opt.value, 10);
      const newPriceCents = parseInt(opt.dataset.priceCents, 10) || 0;
      const newDiscount = Math.floor(newPriceCents * this.discountRate);
      const newFinal = newPriceCents - newDiscount;

      const it = this.missingItems.find((i) => parseInt(i.productId, 10) === partnerProductId);
      if (it) {
        it.variantId = newVariantId;
        it.priceCents = newPriceCents;
      }

      const origEl = card.querySelector('[data-bind="upsell-card-price-original"]');
      const finalEl = card.querySelector('[data-bind="upsell-card-price-final"]');
      if (origEl) origEl.textContent = this._fmt(newPriceCents);
      if (finalEl) finalEl.textContent = this._fmt(newFinal);

      this._recalc();
    }

    _recalc() {
      const missingTotal = this.missingItems.reduce((s, it) => s + (it.priceCents || 0), 0);
      const eligibleTotal = this.cartEligibleCents + missingTotal;
      const discount = Math.floor(eligibleTotal * this.discountRate);
      if (this.savingsEl) {
        this.savingsEl.textContent = this._fmt(discount);
      }
    }

    async activate() {
      this._clearError();
      if (!this.missingItems.length) return;

      this._track('bundle_cart_upsell_cta_click', {
        bundle_type: this.bundleType,
        missing_count: this.missingItems.length,
      });

      this.btn.setAttribute('aria-busy', 'true');
      this.btn.classList.add('is-loading');
      this.btn.disabled = true;

      try {
        const cart =
          document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        const sections =
          cart && typeof cart.getSectionsToRender === 'function'
            ? cart.getSectionsToRender().map((s) => s.id)
            : [];

        const itemsToAdd = this.missingItems.map((it) => ({
          id: parseInt(it.variantId, 10),
          quantity: it.quantity || 1,
        }));

        const res = await fetch(this.cartAddUrl, {
          method: 'POST',
          headers: {
            'X-Requested-With': 'XMLHttpRequest',
            'Content-Type': 'application/json',
            Accept: 'application/json',
          },
          body: JSON.stringify({
            items: itemsToAdd,
            sections,
            sections_url: window.location.pathname,
          }),
        });
        const data = await res.json();

        if (data && data.status) {
          const msg = data.description || data.message || "Erreur lors de l'ajout";
          this._showError(msg);
          this._track('bundle_cart_upsell_error', {
            bundle_type: this.bundleType,
            error_message: msg,
            error_status: data.status,
          });
          return;
        }

        this._track('bundle_cart_upsell_activated', {
          bundle_type: this.bundleType,
          items_added: itemsToAdd.length,
        });

        if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          publish(PUB_SUB_EVENTS.cartUpdate, {
            source: 'bundle-cart-upsell',
            cartData: data,
          });
        }

        if (cart && typeof cart.renderContents === 'function') {
          if (typeof cart.setActiveElement === 'function') {
            cart.setActiveElement(this.btn);
          }
          cart.renderContents(data);
        }
      } catch (e) {
        console.error('[bundle-cart-upsell] exception', e);
        this._showError('Erreur réseau, réessaie dans un instant.');
        try {
          this._track('bundle_cart_upsell_error', {
            bundle_type: this.bundleType,
            error_message: 'network',
            error_status: 0,
          });
        } catch (_) {
          /* silent */
        }
      } finally {
        this.btn.removeAttribute('aria-busy');
        this.btn.classList.remove('is-loading');
        this.btn.disabled = false;
      }
    }

    _showError(msg) {
      if (!this.errorEl) return;
      this.errorEl.textContent = msg;
      this.errorEl.hidden = false;
    }

    _clearError() {
      if (!this.errorEl) return;
      this.errorEl.textContent = '';
      this.errorEl.hidden = true;
    }

    _fmt(cents) {
      return FORMATTER.format((cents || 0) / 100);
    }

    _track(eventName, data) {
      try {
        if (
          window.Shopify &&
          window.Shopify.analytics &&
          typeof window.Shopify.analytics.publish === 'function'
        ) {
          window.Shopify.analytics.publish(eventName, data);
        }
      } catch (e) {
        /* silent */
      }
      try {
        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push(Object.assign({ event: eventName }, data));
      } catch (e) {
        /* silent */
      }
      try {
        if (typeof window.gtag === 'function') {
          window.gtag('event', eventName, data);
        }
      } catch (e) {
        /* silent */
      }
    }
  }

  if (!customElements.get('bundle-cart-upsell')) {
    customElements.define('bundle-cart-upsell', BundleCartUpsell);
  }
})();
